import classNames from 'clsx';
import PropTypes from 'prop-types';
import { isXSmall } from '../../../utils/breakpoints';
import isDesktop from '../../../utils/desktop/isDesktop';
import ScrollReset from '../../shared/ScrollReset';
import { CarouselItems } from './CarouselItems';
import css from './gallery.module.scss';

export default function Carousel({
  showButtons,
  isLeftButtonDisabled,
  isRightButtonDisabled,
  handlePageLeft,
  handlePageRight,
  itemsMatrix,
  numRows,
  page,
  breakpoint,
  isExtendedInfoEnabled,
  imageLoading,
}) {
  const isOnDesktop = isDesktop();

  const shouldShowLeftNavigationButton = showButtons && !isLeftButtonDisabled;
  const shouldShowRightNavigationButton = showButtons && !isRightButtonDisabled;

  const handleLeftClick = () => {
    if (shouldShowLeftNavigationButton) {
      handlePageLeft?.();
    }
  };
  const handleRightClick = () => {
    if (shouldShowRightNavigationButton) {
      handlePageRight?.();
    }
  };

  const handleLeftKeyDown = (event) => {
    if (!shouldShowLeftNavigationButton) {
      return;
    }
    // Trigger handlePageLeft only for 'Enter' or 'Space' key
    if (event.key === 'Enter' || event.key === ' ') {
      handlePageLeft?.();
    }
  };

  const handleRightKeyDown = (event) => {
    if (!shouldShowRightNavigationButton) {
      return;
    }
    // Trigger handlePageRight only for 'Enter' or 'Space' key
    if (event.key === 'Enter' || event.key === ' ') {
      handlePageRight?.();
    }
  };

  return (
    <div className={css.wrapper} data-testid="containerGuideItemsContainer">
      <div
        data-testid="containerLoadMoreLeftLink"
        onClick={handleLeftClick}
        onKeyDown={handleLeftKeyDown}
        /* biome-ignore lint/a11y/useSemanticElements: TODO: explore changing div to button */
        role="button" // A11y
        tabIndex={shouldShowLeftNavigationButton ? 0 : -1} // A11y
        className={classNames(css.pager, css.left, {
          [css.desktopPager]: isOnDesktop,
          [css.pagerHidden]: !shouldShowLeftNavigationButton,
        })}
      >
        <i className={classNames(css.chevronLeft, css.chevron)} />
      </div>
      <ScrollReset
        scrollHash={isXSmall(breakpoint)}
        className={classNames(css.container, {
          [css.enableHorizontalScroll]: !showButtons,
        })}
      >
        <CarouselItems
          itemsMatrix={itemsMatrix}
          numRows={numRows}
          className={css.pad}
          page={page}
          breakpoint={breakpoint}
          isExtendedInfoEnabled={isExtendedInfoEnabled}
          imageLoading={imageLoading}
        />
      </ScrollReset>
      <div
        data-testid="containerLoadMoreRightLink"
        onClick={handleRightClick}
        onKeyDown={handleRightKeyDown}
        /* biome-ignore lint/a11y/useSemanticElements: TODO: explore changing div to button */
        role="button" // A11y
        tabIndex={shouldShowRightNavigationButton ? 0 : -1} // A11y
        className={classNames(css.pager, css.right, {
          [css.desktopPager]: isOnDesktop,
          [css.pagerHidden]: !shouldShowRightNavigationButton,
        })}
      >
        <i className={classNames(css.chevronRight, css.chevron)} />
      </div>
    </div>
  );
}

Carousel.propTypes = {
  numRows: PropTypes.number.isRequired,
  page: PropTypes.number.isRequired,
  itemsMatrix: PropTypes.array.isRequired,
  breakpoint: PropTypes.number.isRequired,
  showButtons: PropTypes.bool.isRequired,
  isLeftButtonDisabled: PropTypes.bool.isRequired,
  isRightButtonDisabled: PropTypes.bool.isRequired,
  isExtendedInfoEnabled: PropTypes.bool,
  imageLoading: PropTypes.string,
  handlePageLeft: PropTypes.func.isRequired,
  handlePageRight: PropTypes.func.isRequired,
};
